import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Upload, Download, FileText, AlertCircle, CheckCircle, Database, Package } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { dataSyncEvents } from '@/utils/dataSyncEvents';
import { useAuth } from '@/components/Auth/AuthProvider';
import { DataTaggingComponent } from '@/components/DataManagement/DataTaggingComponent';
import { useDownloadGate } from '@/components/DownloadGate/useDownloadGate';

interface DataUploadFormProps {
  onSuccess?: () => void;
}

export const DataUploadForm = ({ onSuccess }: DataUploadFormProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState<'metrics' | 'materials'>('metrics');
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { guardDownload } = useDownloadGate();

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const downloadTemplate = async (type: 'metrics' | 'materials', format: 'csv' | 'excel') => {
    if (!(await guardDownload())) return;

    try {
      setProcessing(true);
      
      let headers: string[] = [];
      let sampleData: any[] = [];

      if (type === 'metrics') {
        // Get sample metrics from unified_metrics_library table
        const { data: metrics } = await supabase
          .from('unified_metrics_library')
          .select('*')
          .limit(5);

        headers = [
          'library_metric_id',
          'name',
          'category',
          'subcategory',
          'unit',
          'scope',
          'custom_value',
          'reporting_period',
          'data_source',
          'description',
          'currency',
          'validation_status',
          'notes'
        ];
        
        sampleData = (metrics || []).map(metric => ({
          library_metric_id: metric.id,
          name: metric.name,
          category: metric.category,
          subcategory: metric.subcategory,
          unit: metric.unit,
          scope: metric.scope,
          custom_value: '1500',
          reporting_period: new Date().toISOString().split('T')[0],
          data_source: 'CSV Import',
          description: metric.description,
          currency: metric.currency || 'USD',
          validation_status: 'pending',
          notes: 'Imported via CSV upload'
        }));
      } else {
        // Get sample materials from unified_materials_library table
        const { data: materials } = await supabase
          .from('unified_materials_library')
          .select('*')
          .limit(5);

        headers = [
          'library_material_id',
          'name',
          'category',
          'subcategory',
          'description',
          'unit',
          'density',
          'carbon_footprint',
          'cost_per_unit',
          'currency',
          'custom_density',
          'custom_carbon_footprint',
          'custom_cost_per_unit',
          'custom_unit',
          'custom_currency',
          'supplier_info',
          'sustainability_rating',
          'certifications',
          'emission_factor',
          'calorific_value',
          'scope',
          'environmental_impact_score',
          'recyclability',
          'biodegradability',
          'availability',
          'lead_time',
          'minimum_order_quantity',
          'material_type',
          'composition',
          'strength',
          'durability',
          'thermal_properties',
          'chemical_resistance',
          'reporting_period',
          'data_source',
          'validation_status',
          'notes'
        ];
        
        sampleData = (materials || []).map(material => ({
          library_material_id: material.id,
          name: material.name,
          category: material.category,
          subcategory: material.subcategory,
          description: material.description || '',
          unit: material.unit || 'kg',
          density: material.density || '',
          carbon_footprint: material.carbon_footprint || '',
          cost_per_unit: material.cost_per_unit || '',
          currency: material.currency || 'USD',
          // Custom fields - leave empty for template
          custom_density: '',
          custom_carbon_footprint: '',
          custom_cost_per_unit: '',
          custom_unit: '',
          custom_currency: 'USD',
          supplier_info: material.supplier_info || '',
          sustainability_rating: material.sustainability_rating || '',
          certifications: Array.isArray(material.certifications) ? material.certifications.join(';') : '',
          emission_factor: material.emission_factor || '',
          calorific_value: material.calorific_value || '',
          scope: material.scope || '',
          environmental_impact_score: material.environmental_impact_score || '',
          recyclability: material.recyclability || '',
          biodegradability: material.biodegradability || '',
          availability: material.availability || '',
          lead_time: material.lead_time || '',
          minimum_order_quantity: material.minimum_order_quantity || '',
          material_type: material.material_type || '',
          composition: material.composition || '',
          strength: material.strength || '',
          durability: material.durability || '',
          thermal_properties: material.thermal_properties || '',
          chemical_resistance: material.chemical_resistance || '',
          reporting_period: new Date().toISOString().split('T')[0],
          data_source: 'CSV Import',
          validation_status: 'pending',
          notes: 'Imported via CSV upload'
        }));
      }

      // Create file content
      let content: string;
      let mimeType: string;
      let fileExtension: string;

      if (format === 'csv') {
        content = [
          headers.join(','),
          ...sampleData.map(row => 
            headers.map(header => {
              const value = row[header];
              if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                return `"${value.replace(/"/g, '""')}"`;
              }
              return value || '';
            }).join(',')
          )
        ].join('\n');
        mimeType = 'text/csv;charset=utf-8;';
        fileExtension = 'csv';
      } else {
        // Create TSV for Excel
        content = [
          headers.join('\t'),
          ...sampleData.map(row => 
            headers.map(header => row[header] || '').join('\t')
          )
        ].join('\n');
        mimeType = 'text/tab-separated-values;charset=utf-8;';
        fileExtension = 'xls';
      }

      // Download file
      const blob = new Blob([content], { type: mimeType });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${type}_import_template.${fileExtension}`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: "Template Downloaded",
        description: `${type.charAt(0).toUpperCase() + type.slice(1)} template has been downloaded successfully.`
      });
    } catch (error) {
      console.error('Error downloading template:', error);
      toast({
        title: "Download Error",
        description: "Failed to download template. Please try again.",
        variant: "destructive"
      });
    } finally {
      setProcessing(false);
    }
  };

  const saveMetricToSupabase = async (rowData: any, userId: string) => {
    try {
      const { data: existingLibrary, error: libraryError } = await supabase
        .from('unified_metrics_library')
        .select('id')
        .eq('id', rowData.library_metric_id)
        .single();

      if (libraryError && !rowData.name) {
        throw new Error('Either provide a valid library_metric_id or specify name, category, and unit');
      }

      const metricData = {
        organization_id: userId,
        name: rowData.name || existingLibrary?.name,
        category: rowData.category || existingLibrary?.category,
        subcategory: rowData.subcategory || existingLibrary?.subcategory,
        description: rowData.description || existingLibrary?.description,
        unit: rowData.unit || existingLibrary?.unit,
        currency: rowData.currency || existingLibrary?.currency || 'USD',
        custom_value: parseFloat(rowData.custom_value) || 0,
        custom_metadata: {
          reporting_period: rowData.reporting_period || new Date().toISOString().split('T')[0],
          data_source: rowData.data_source || 'CSV Import',
          scope: rowData.scope || existingLibrary?.scope,
          validation_status: rowData.validation_status || 'pending',
          notes: rowData.notes || '',
          library_metric_id: rowData.library_metric_id || null
        },
        is_custom: !rowData.library_metric_id
      };

      const { data: metric, error } = await supabase
        .from('user_metrics_data')
        .insert([metricData])
        .select()
        .single();

      if (error) throw error;

      // Emit sync event
      dataSyncEvents.emitMetricChange('created', metric.id, userId);
      
      return { success: true, data: metric };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const saveMaterialToSupabase = async (rowData: any, userId: string) => {
    try {
      const { data: existingLibrary, error: libraryError } = await supabase
        .from('unified_materials_library')
        .select('id')
        .eq('id', rowData.library_material_id)
        .single();

      if (libraryError && !rowData.name) {
        throw new Error('Either provide a valid library_material_id or specify name and category');
      }

      const materialData = {
        organization_id: userId,
        name: rowData.name || existingLibrary?.name,
        category: rowData.category || existingLibrary?.category,
        subcategory: rowData.subcategory || existingLibrary?.subcategory,
        description: rowData.description || existingLibrary?.description,
        unit: rowData.unit || existingLibrary?.unit || rowData.custom_unit || 'kg',
        currency: rowData.currency || existingLibrary?.currency || rowData.custom_currency || 'USD',
        density: rowData.custom_density ? parseFloat(rowData.custom_density) : 
                (existingLibrary?.density ? parseFloat(existingLibrary.density) : null),
        carbon_footprint: rowData.custom_carbon_footprint ? parseFloat(rowData.custom_carbon_footprint) :
                         (existingLibrary?.carbon_footprint ? parseFloat(existingLibrary.carbon_footprint) : null),
        cost_per_unit: rowData.custom_cost_per_unit ? parseFloat(rowData.custom_cost_per_unit) :
                      (existingLibrary?.cost_per_unit ? parseFloat(existingLibrary.cost_per_unit) : null),
        custom_metadata: {
          reporting_period: rowData.reporting_period || new Date().toISOString().split('T')[0],
          data_source: rowData.data_source || 'CSV Import',
          validation_status: rowData.validation_status || 'pending',
          notes: rowData.notes || '',
          library_material_id: rowData.library_material_id || null,
          supplier_info: rowData.supplier_info || existingLibrary?.supplier_info,
          sustainability_rating: rowData.sustainability_rating || existingLibrary?.sustainability_rating,
          certifications: rowData.certifications ? rowData.certifications.split(';').filter(Boolean) : 
                        (Array.isArray(existingLibrary?.certifications) ? existingLibrary.certifications : []),
          emission_factor: rowData.emission_factor || existingLibrary?.emission_factor,
          calorific_value: rowData.calorific_value || existingLibrary?.calorific_value,
          scope: rowData.scope || existingLibrary?.scope,
          environmental_impact_score: rowData.environmental_impact_score || existingLibrary?.environmental_impact_score,
          recyclability: rowData.recyclability || existingLibrary?.recyclability,
          biodegradability: rowData.biodegradability || existingLibrary?.biodegradability,
          availability: rowData.availability || existingLibrary?.availability,
          lead_time: rowData.lead_time || existingLibrary?.lead_time,
          minimum_order_quantity: rowData.minimum_order_quantity || existingLibrary?.minimum_order_quantity,
          material_type: rowData.material_type || existingLibrary?.material_type,
          composition: rowData.composition || existingLibrary?.composition,
          strength: rowData.strength || existingLibrary?.strength,
          durability: rowData.durability || existingLibrary?.durability,
          thermal_properties: rowData.thermal_properties || existingLibrary?.thermal_properties,
          chemical_resistance: rowData.chemical_resistance || existingLibrary?.chemical_resistance
        },
        is_custom: !rowData.library_material_id || 
                  (rowData.custom_density || rowData.custom_carbon_footprint || rowData.custom_cost_per_unit)
      };

      const { data: material, error } = await supabase
        .from('user_materials_data')
        .insert([materialData])
        .select()
        .single();

      if (error) throw error;

      // Emit sync event
      dataSyncEvents.emitMaterialChange('created', material.id, userId);
      
      return { success: true, data: material };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  };

  const processUpload = async () => {
    if (!file || !user) return;

    setUploading(true);
    try {
      // Validate file
      if (file.size === 0) {
        throw new Error('The uploaded file is empty. Please select a valid file.');
      }
      
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size exceeds 10MB limit. Please use a smaller file.');
      }

      // Read file
      let text: string;
      try {
        text = await file.text();
        if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
          text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        }
      } catch (fileError) {
        // Fallback FileReader
        text = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            if (e.target?.result) {
              resolve(e.target.result as string);
            } else {
              reject(new Error('Failed to read file'));
            }
          };
          reader.onerror = () => reject(new Error('FileReader error'));
          reader.readAsText(file);
        });
        
        if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
          text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        }
      }
      
      // Validate content
      if (!text || text.trim().length === 0) {
        throw new Error('The uploaded file appears to be empty.');
      }
      
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        throw new Error('File must contain at least a header row and one data row');
      }

      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const dataRows = lines.slice(1);
      
      console.log('Processing upload:', {
        totalLines: lines.length,
        headers: headers,
        dataRowCount: dataRows.length
      });
      
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      // Process each row
      for (let i = 0; i < dataRows.length; i++) {
        const line = dataRows[i].trim();
        if (!line) continue;

        try {
          // Parse CSV row
          const values: string[] = [];
          let current = '';
          let inQuotes = false;
          
          for (let j = 0; j < line.length; j++) {
            const char = line[j];
            if (char === '"') {
              inQuotes = !inQuotes;
            } else if (char === ',' && !inQuotes) {
              values.push(current.trim());
              current = '';
            } else {
              current += char;
            }
          }
          values.push(current.trim());

          // Create data object
          const rowData: any = {};
          headers.forEach((header, index) => {
            rowData[header] = values[index] || '';
          });

          console.log(`Processing row ${i + 2}:`, rowData);

          let result;
          if (uploadType === 'metrics') {
            result = await saveMetricToSupabase(rowData, user.id);
          } else {
            result = await saveMaterialToSupabase(rowData, user.id);
          }

          if (result.success) {
            successCount++;
          } else {
            errorCount++;
            errors.push(`Row ${i + 2}: ${result.error}`);
          }
        } catch (rowError: any) {
          errorCount++;
          errors.push(`Row ${i + 2}: ${rowError.message || 'Unknown error'}`);
        }
      }

      // Show results
      if (successCount > 0) {
        toast({
          title: "Upload Successful",
          description: `Successfully imported ${successCount} ${uploadType}.${errorCount > 0 ? ` ${errorCount} rows had errors.` : ''}`,
        });
      } else {
        console.error('Upload failed. Errors:', errors);
        throw new Error(`No data was successfully imported. ${errorCount} rows had errors.`);
      }

      if (errorCount > 0) {
        console.error('Upload errors:', errors);
      }

      setFile(null);
      onSuccess?.();
    } catch (error: any) {
      console.error('Error processing upload:', error);
      toast({
        title: "Upload Error",
        description: `Failed to process upload: ${error.message || 'Unknown error'}`,
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Template Downloads */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Download Templates
          </CardTitle>
          <CardDescription>
            Download CSV/Excel templates for bulk data upload
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="upload-type">Data Type</Label>
            <Select value={uploadType} onValueChange={(value: 'metrics' | 'materials') => setUploadType(value)}>
              <SelectTrigger id="upload-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="metrics">
                  <div className="flex items-center gap-2">
                    <Database className="h-4 w-4" />
                    Sustainability Metrics
                  </div>
                </SelectItem>
                <SelectItem value="materials">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Materials Library
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium">CSV Template</Label>
              <p className="text-xs text-gray-600 mb-2">
                Download a CSV template with the exact column structure
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => downloadTemplate(uploadType, 'csv')}
                disabled={processing}
              >
                <Download className="h-4 w-4 mr-2" />
                {processing ? 'Processing...' : `Download ${uploadType} CSV Template`}
              </Button>
            </div>

            <div>
              <Label className="text-sm font-medium">Excel Template</Label>
              <p className="text-xs text-gray-600 mb-2">
                Download an Excel template with sample data
              </p>
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => downloadTemplate(uploadType, 'excel')}
                disabled={processing}
              >
                <FileText className="h-4 w-4 mr-2" />
                {processing ? 'Processing...' : `Download ${uploadType} Excel Template`}
              </Button>
            </div>
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <p><strong>Template Features:</strong></p>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>Exact column structure matching Supabase tables</li>
                  <li>Sample data from your database</li>
                  <li>Required and optional field indicators</li>
                  <li>Links to library items when available</li>
                </ul>
              </div>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* File Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Upload Data
          </CardTitle>
          <CardDescription>
            Upload your CSV or Excel file to save directly to Supabase
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="file-upload">Choose File</Label>
              <Input
                id="file-upload"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileUpload}
                className="mt-1"
              />
            </div>
            
            {file && (
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-md">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm text-green-700">{file.name}</span>
                <Badge variant="outline" className="text-xs">
                  {(file.size / 1024).toFixed(1)} KB
                </Badge>
              </div>
            )}

            <div>
              <Label className="text-sm font-medium">Apply Tags to All Uploaded Data</Label>
              <p className="text-xs text-gray-600 mb-2">
                Tags will be applied to all {uploadType} data uploaded from this file
              </p>
              <DataTaggingComponent
                selectedTags={[]}
                onTagsChange={() => {}}
                dataType={uploadType === 'metrics' ? 'metric' : 'material'}
                showCreateTag={true}
              />
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <p><strong>Supported formats:</strong> CSV, Excel (.xlsx, .xls)</p>
                  <p><strong>File size limit:</strong> 10 MB</p>
                  <p><strong>Important:</strong> Data is saved directly to Supabase in real-time</p>
                </div>
              </AlertDescription>
            </Alert>

            <Button 
              className="w-full" 
              disabled={!file || uploading}
              onClick={processUpload}
            >
              {uploading ? (
                <>
                  <Upload className="h-4 w-4 mr-2 animate-spin" />
                  Uploading to Supabase...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload to Supabase
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Upload Guidelines */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>Upload Guidelines</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Database className="h-4 w-4" />
                Metrics Upload (user_metrics_data table)
              </h4>
              <ul className="space-y-2 text-sm">
                <li>• Data is saved directly to Supabase <code>user_metrics_data</code> table</li>
                <li>• Provide <code>library_metric_id</code> to link to library metrics</li>
                <li>• Or specify <code>name</code>, <code>category</code>, <code>unit</code> for custom metrics</li>
                <li>• <code>custom_value</code> is required with numeric data</li>
                <li>• <code>reporting_period</code> in YYYY-MM-DD format</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2">
                <Package className="h-4 w-4" />
                Materials Upload (user_materials_data table)
              </h4>
              <ul className="space-y-2 text-sm">
                <li>• Data is saved directly to Supabase <code>user_materials_data</code> table</li>
                <li>• Provide <code>library_material_id</code> to link to library materials</li>
                <li>• Or specify <code>name</code>, <code>category</code> for custom materials</li>
                <li>• Use <code>custom_</code> fields to override library values</li>
                <li>• Supports all material properties from the library</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};






// import React, { useState } from 'react';
// import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
// import { Button } from '@/components/ui/button';
// import { Input } from '@/components/ui/input';
// import { Label } from '@/components/ui/label';
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
// import { Alert, AlertDescription } from '@/components/ui/alert';
// import { Badge } from '@/components/ui/badge';
// import { Upload, Download, FileText, AlertCircle, CheckCircle, Database, Package } from 'lucide-react';
// import { useToast } from '@/hooks/use-toast';
// import { enhancedOrganizationalDataService } from '@/services/enhancedOrganizationalDataService';
// import { indexedDBService } from '@/services/indexedDBService';
// import { dataSyncEvents } from '@/utils/dataSyncEvents';
// import { useAuth } from '@/components/Auth/AuthProvider';
// import { UnifiedMetric, UnifiedMaterial } from '@/types/unifiedLibrary';
// import { Textarea } from '@/components/ui/textarea';
// import { DataTaggingComponent } from '@/components/DataManagement/DataTaggingComponent';


// interface DataUploadFormProps {
//   onSuccess?: () => void;
// }

// export const DataUploadForm = ({ onSuccess }: DataUploadFormProps) => {
//   const [file, setFile] = useState<File | null>(null);
//   const [uploadType, setUploadType] = useState<'metrics' | 'materials'>('metrics');
//   const [uploading, setUploading] = useState(false);
//   const [processing, setProcessing] = useState(false);
//   const { toast } = useToast();
//   const { user } = useAuth();

//   const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
//     const selectedFile = event.target.files?.[0];
//     if (selectedFile) {
//       setFile(selectedFile);
//     }
//   };

//   const downloadTemplate = async (type: 'metrics' | 'materials', format: 'csv' | 'excel') => {
//     try {
//       setProcessing(true);
      
//       let headers: string[] = [];
//       let sampleData: any[] = [];

//       if (type === 'metrics') {
//         // Get sample metrics from library - using a mock for now since we're not using unifiedLibraryService
//         const metrics = [
//           {
//             id: 'metric_1',
//             name: 'Energy Consumption',
//             category: 'Environmental',
//             subcategory: 'Energy',
//             unit: 'kWh',
//             scope: 'Scope 1',
//             description: 'Total energy consumption'
//           }
//         ];
//         headers = [
//           'metric_id',
//           'name',
//           'category',
//           'subcategory',
//           'unit',
//           'scope',
//           'custom_value',
//           'reporting_period',
//           'data_source',
//           'description',
//           'validation_status',
//           'custom_metadata'
//         ];
        
//         sampleData = metrics.map(metric => ({
//           metric_id: metric.id,
//           name: metric.name,
//           category: metric.category,
//           subcategory: metric.subcategory,
//           unit: metric.unit,
//           scope: metric.scope,
//           custom_value: '1500', // Valid numeric value that can be uploaded
//           reporting_period: new Date().toISOString().split('T')[0],
//           data_source: 'CSV Import',
//           description: metric.description,
//           validation_status: 'pending',
//           custom_metadata: '{"notes":"Sample metadata","source":"CSV Import"}'
//         }));
//       } else {
//         // Get sample materials from library with all new columns
//         const materials = [
//           {
//             id: 'material_1',
//             name: 'Steel',
//             category: 'Metals',
//             subcategory: 'Ferrous Metals',
//             unit: 'kg',
//             density: 7850,
//             carbon_footprint: 1.8,
//             cost_per_unit: 0.8,
//             currency: 'USD',
//             supplier_info: 'SteelCorp Industries',
//             sustainability_rating: 3,
//             certifications: ['ISO 9001', 'ASTM Certified'],
//             emission_factor: 1.8,
//             calorific_value: null,
//             scope: 'Scope 3',
//             environmental_impact_score: 85.0,
//             recyclability: 'High',
//             biodegradability: 'Non-Biodegradable',
//             availability: 'Readily Available',
//             lead_time: '1-2 weeks',
//             minimum_order_quantity: '5000 kg',
//             material_type: 'Ferrous Metal',
//             composition: 'Iron-carbon alloy with additional elements',
//             strength: 'Very high tensile strength',
//             durability: 'Excellent structural integrity',
//             thermal_properties: 'Good thermal conductivity',
//             chemical_resistance: 'Susceptible to rust, requires protection',
//             notes: 'Primary material for structural applications',
//             description: 'Steel material for construction and manufacturing'
//           }
//         ];
//         headers = [
//           'material_id',
//           'name',
//           'category',
//           'subcategory',
//           'description',
//           'unit',
//           'density',
//           'carbon_footprint',
//           'cost_per_unit',
//           'currency',
//           'supplier_info',
//           'sustainability_rating',
//           'certifications',
//           'emission_factor',
//           'calorific_value',
//           'scope',
//           'environmental_impact_score',
//           'recyclability',
//           'biodegradability',
//           'availability',
//           'lead_time',
//           'minimum_order_quantity',
//           'material_type',
//           'composition',
//           'strength',
//           'durability',
//           'thermal_properties',
//           'chemical_resistance',
//           'notes',
//           'custom_density',
//           'custom_carbon_footprint',
//           'custom_cost_per_unit',
//           'custom_unit',
//           'custom_currency',
//           'custom_environmental_impact_score',
//           'custom_recyclability',
//           'custom_biodegradability',
//           'custom_availability',
//           'custom_lead_time',
//           'custom_minimum_order_quantity',
//           'custom_material_type',
//           'custom_composition',
//           'custom_strength',
//           'custom_durability',
//           'custom_thermal_properties',
//           'custom_chemical_resistance',
//           'custom_notes',
//           'custom_metadata'
//         ];
        
//         sampleData = materials.map(material => ({
//           material_id: material.id,
//           name: material.name,
//           category: material.category,
//           subcategory: material.subcategory,
//           description: material.description,
//           unit: material.unit,
//           density: material.density,
//           carbon_footprint: material.carbon_footprint,
//           cost_per_unit: material.cost_per_unit,
//           currency: material.currency,
//           supplier_info: material.supplier_info,
//           sustainability_rating: material.sustainability_rating,
//           certifications: material.certifications ? material.certifications.join(';') : '',
//           emission_factor: material.emission_factor,
//           calorific_value: material.calorific_value || '',
//           scope: material.scope,
//           environmental_impact_score: material.environmental_impact_score,
//           recyclability: material.recyclability,
//           biodegradability: material.biodegradability,
//           availability: material.availability,
//           lead_time: material.lead_time,
//           minimum_order_quantity: material.minimum_order_quantity,
//           material_type: material.material_type,
//           composition: material.composition,
//           strength: material.strength,
//           durability: material.durability,
//           thermal_properties: material.thermal_properties,
//           chemical_resistance: material.chemical_resistance,
//           notes: material.notes,
//           // Custom fields - leave empty for template (user will fill these)
//           custom_density: '',
//           custom_carbon_footprint: '',
//           custom_cost_per_unit: '',
//           custom_unit: '',
//           custom_currency: 'USD',
//           custom_environmental_impact_score: '',
//           custom_recyclability: '',
//           custom_biodegradability: '',
//           custom_availability: '',
//           custom_lead_time: '',
//           custom_minimum_order_quantity: '',
//           custom_material_type: '',
//           custom_composition: '',
//           custom_strength: '',
//           custom_durability: '',
//           custom_thermal_properties: '',
//           custom_chemical_resistance: '',
//           custom_notes: '',
//           custom_metadata: '{"notes":"Sample metadata","source":"CSV Import","reporting_period":"' + new Date().toISOString().split('T')[0] + '","data_source":"CSV Import","validation_status":"pending"}'
//         }));
//       }

//       if (format === 'csv') {
//         // Create CSV content
//         const csvContent = [
//           headers.join(','),
//           ...sampleData.map(row => 
//             headers.map(header => {
//               const value = row[header];
//               // Escape commas and quotes in CSV
//               if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
//                 return `"${value.replace(/"/g, '""')}"`;
//               }
//               return value || '';
//             }).join(',')
//           )
//         ].join('\n');

//         // Download CSV file
//         const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
//         const link = document.createElement('a');
//         const url = URL.createObjectURL(blob);
//         link.setAttribute('href', url);
//         link.setAttribute('download', `${type}_import_template.csv`);
//         link.style.visibility = 'hidden';
//         document.body.appendChild(link);
//         link.click();
//         document.body.removeChild(link);
//       } else {
//         // For Excel, we'll create a simple CSV that can be opened in Excel
//         // In a real implementation, you'd use a library like xlsx
//         const csvContent = [
//           headers.join('\t'),
//           ...sampleData.map(row => 
//             headers.map(header => row[header] || '').join('\t')
//           )
//         ].join('\n');

//         const blob = new Blob([csvContent], { type: 'text/tab-separated-values;charset=utf-8;' });
//         const link = document.createElement('a');
//         const url = URL.createObjectURL(blob);
//         link.setAttribute('href', url);
//         link.setAttribute('download', `${type}_import_template.xls`);
//         link.style.visibility = 'hidden';
//         document.body.appendChild(link);
//         link.click();
//         document.body.removeChild(link);
//       }

//       toast({
//         title: "Template Downloaded",
//         description: `${type.charAt(0).toUpperCase() + type.slice(1)} template has been downloaded successfully.`
//       });
//     } catch (error) {
//       console.error('Error downloading template:', error);
//       toast({
//         title: "Download Error",
//         description: "Failed to download template. Please try again.",
//         variant: "destructive"
//       });
//     } finally {
//       setProcessing(false);
//     }
//   };

//   const processUpload = async () => {
//     if (!file || !user) return;

//     setUploading(true);
//     try {
//       // Validate file before processing
//       if (file.size === 0) {
//         throw new Error('The uploaded file is empty. Please select a valid file.');
//       }
      
//       if (file.size > 10 * 1024 * 1024) { // 10MB limit
//         throw new Error('File size exceeds 10MB limit. Please use a smaller file.');
//       }

//       // Initialize IndexedDB service
//       await indexedDBService.initialize();
//       await enhancedOrganizationalDataService.initialize();

//       // Read the file with better error handling
//       let text: string;
//       try {
//         console.log('Attempting to read file:', {
//           name: file.name,
//           size: file.size,
//           type: file.type,
//           lastModified: file.lastModified
//         });
        
//         if (file.name.endsWith('.csv')) {
//           text = await file.text();
//         } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
//           // For Excel files, we'll try to read as text (basic support)
//           // In a production environment, you'd use a library like xlsx
//           text = await file.text();
//           // Remove any Excel-specific formatting
//           text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
//         } else {
//           throw new Error('Unsupported file format. Please use CSV or Excel files.');
//         }
        
//         console.log('File read successfully, content length:', text.length);
//       } catch (fileError) {
//         console.error('File reading error:', fileError);
        
//         // Try fallback method using FileReader
//         try {
//           console.log('Attempting fallback file reading method...');
//           text = await new Promise((resolve, reject) => {
//             const reader = new FileReader();
//             reader.onload = (e) => {
//               if (e.target?.result) {
//                 resolve(e.target.result as string);
//               } else {
//                 reject(new Error('FileReader failed to read file'));
//               }
//             };
//             reader.onerror = () => reject(new Error('FileReader error'));
//             reader.readAsText(file);
//           });
          
//           if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
//             text = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
//           }
          
//           console.log('Fallback file reading successful, content length:', text.length);
//         } catch (fallbackError) {
//           console.error('Fallback file reading also failed:', fallbackError);
//           throw new Error(`Failed to read file: ${fileError instanceof Error ? fileError.message : 'Unknown file reading error'}. Please ensure the file is not corrupted and try again.`);
//         }
//       }
      
//       // Validate file content
//       if (!text || text.trim().length === 0) {
//         throw new Error('The uploaded file appears to be empty. Please check the file and try again.');
//       }
      
//       const lines = text.split('\n').filter(line => line.trim());
      
//       if (lines.length < 2) {
//         throw new Error('File must contain at least a header row and one data row');
//       }

//       const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
//       const dataRows = lines.slice(1);
      
//       console.log('File processing info:', {
//         totalLines: lines.length,
//         headerCount: headers.length,
//         dataRowCount: dataRows.length,
//         headers: headers
//       });
      
//       let successCount = 0;
//       let errorCount = 0;
//       const errors: string[] = [];

//       // Process each row
//       for (let i = 0; i < dataRows.length; i++) {
//         const line = dataRows[i].trim();
//         if (!line) continue;

//         try {
//           // Parse CSV row (handle quoted values)
//           const values: string[] = [];
//           let current = '';
//           let inQuotes = false;
          
//           for (let j = 0; j < line.length; j++) {
//             const char = line[j];
//             if (char === '"') {
//               inQuotes = !inQuotes;
//             } else if (char === ',' && !inQuotes) {
//               values.push(current.trim());
//               current = '';
//             } else {
//               current += char;
//             }
//           }
//           values.push(current.trim());

//           // Create data object
//           const rowData: any = {};
//           headers.forEach((header, index) => {
//             rowData[header] = values[index] || '';
//           });

//           // Debug logging
//           console.log(`Processing row ${i + 2}:`, { headers, values, rowData });

//           if (uploadType === 'metrics') {
//             // Process metric data
//             console.log('Processing metric row:', { name: rowData.name, custom_value: rowData.custom_value });
//             if (rowData.name && rowData.custom_value) {
//               const result = await enhancedOrganizationalDataService.createUserMetric(user.id, {
//                 organization_id: user.id,
//                 metric_id: rowData.metric_id || `metric_${Date.now()}_${i}`,
//                 name: rowData.name,
//                 category: rowData.category || 'Environmental',
//                 subcategory: rowData.subcategory || 'General',
//                 description: rowData.description || '',
//                 unit: rowData.unit || '',
//                 currency: rowData.currency || 'USD',
//                 custom_value: parseFloat(rowData.custom_value) || 0,
//                 custom_metadata: {
//                   reporting_period: rowData.reporting_period || new Date().toISOString().split('T')[0],
//                   data_source: rowData.data_source || 'CSV Import',
//                   scope: rowData.scope || null,
//                   validation_status: rowData.validation_status || 'pending',
//                   ...(rowData.custom_metadata ? (() => {
//                     try {
//                       return JSON.parse(rowData.custom_metadata);
//                     } catch (e) {
//                       console.warn(`Failed to parse custom_metadata for row ${i + 2}:`, e);
//                       return {};
//                     }
//                   })() : {})
//                 },
//                 is_custom: true
//               });

//               if (result.success) {
//                 successCount++;
//                 // Emit event to notify other components
//                 if (result.metric) {
//                   dataSyncEvents.emitMetricChange('created', result.metric.id, user.id);
//                 }
//               } else {
//                 errorCount++;
//                 errors.push(`Row ${i + 2}: ${result.error}`);
//               }
//             } else {
//               errorCount++;
//               errors.push(`Row ${i + 2}: Missing required fields (name, custom_value)`);
//             }
//           } else {
//             // Process material data
//             console.log('Processing material row:', { name: rowData.name, category: rowData.category });
//             if (rowData.name) {
//               // Determine if this is a custom material or library material
//               const isCustomMaterial = rowData.custom_density || rowData.custom_carbon_footprint || rowData.custom_cost_per_unit || 
//                                      rowData.custom_environmental_impact_score || rowData.custom_recyclability || rowData.custom_biodegradability ||
//                                      rowData.custom_availability || rowData.custom_lead_time || rowData.custom_minimum_order_quantity ||
//                                      rowData.custom_material_type || rowData.custom_composition || rowData.custom_strength ||
//                                      rowData.custom_durability || rowData.custom_thermal_properties || rowData.custom_chemical_resistance ||
//                                      rowData.custom_notes;

//               const result = await enhancedOrganizationalDataService.createUserMaterial(user.id, {
//                 organization_id: user.id,
//                 material_id: rowData.material_id || `material_${Date.now()}_${i}`,
//                 name: rowData.name,
//                 category: rowData.category || 'Materials',
//                 subcategory: rowData.subcategory || 'General',
//                 description: rowData.description || '',
//                 unit: rowData.unit || '',
//                 currency: rowData.currency || 'USD',
//                 custom_density: rowData.custom_density ? parseFloat(rowData.custom_density) : undefined,
//                 custom_carbon_footprint: rowData.custom_carbon_footprint ? parseFloat(rowData.custom_carbon_footprint) : undefined,
//                 custom_cost_per_unit: rowData.custom_cost_per_unit ? parseFloat(rowData.custom_cost_per_unit) : undefined,
//                 custom_metadata: {
//                   reporting_period: new Date().toISOString().split('T')[0],
//                   data_source: 'CSV Import',
//                   validation_status: 'pending',
//                   // Add all new custom fields
//                   custom_environmental_impact_score: rowData.custom_environmental_impact_score ? parseFloat(rowData.custom_environmental_impact_score) : undefined,
//                   custom_recyclability: rowData.custom_recyclability || undefined,
//                   custom_biodegradability: rowData.custom_biodegradability || undefined,
//                   custom_availability: rowData.custom_availability || undefined,
//                   custom_lead_time: rowData.custom_lead_time || undefined,
//                   custom_minimum_order_quantity: rowData.custom_minimum_order_quantity || undefined,
//                   custom_material_type: rowData.custom_material_type || undefined,
//                   custom_composition: rowData.custom_composition || undefined,
//                   custom_strength: rowData.custom_strength || undefined,
//                   custom_durability: rowData.custom_durability || undefined,
//                   custom_thermal_properties: rowData.custom_thermal_properties || undefined,
//                   custom_chemical_resistance: rowData.custom_chemical_resistance || undefined,
//                   custom_notes: rowData.custom_notes || undefined,
//                   // Include library data if provided (for reference)
//                   library_density: rowData.density ? parseFloat(rowData.density) : undefined,
//                   library_carbon_footprint: rowData.carbon_footprint ? parseFloat(rowData.carbon_footprint) : undefined,
//                   library_cost_per_unit: rowData.cost_per_unit ? parseFloat(rowData.cost_per_unit) : undefined,
//                   library_environmental_impact_score: rowData.environmental_impact_score ? parseFloat(rowData.environmental_impact_score) : undefined,
//                   library_recyclability: rowData.recyclability || undefined,
//                   library_biodegradability: rowData.biodegradability || undefined,
//                   library_availability: rowData.availability || undefined,
//                   library_lead_time: rowData.lead_time || undefined,
//                   library_minimum_order_quantity: rowData.minimum_order_quantity || undefined,
//                   library_material_type: rowData.material_type || undefined,
//                   library_composition: rowData.composition || undefined,
//                   library_strength: rowData.strength || undefined,
//                   library_durability: rowData.durability || undefined,
//                   library_thermal_properties: rowData.thermal_properties || undefined,
//                   library_chemical_resistance: rowData.chemical_resistance || undefined,
//                   library_notes: rowData.notes || undefined,
//                   ...(rowData.custom_metadata ? (() => {
//                     try {
//                       return JSON.parse(rowData.custom_metadata);
//                     } catch (e) {
//                       console.warn(`Failed to parse custom_metadata for row ${i + 2}:`, e);
//                       return {};
//                     }
//                   })() : {})
//                 },
//                 is_custom: isCustomMaterial
//               });

//               if (result.success) {
//                 successCount++;
//                 // Emit event to notify other components
//                 if (result.material) {
//                   dataSyncEvents.emitMaterialChange('created', result.material.id, user.id);
//                 }
//               } else {
//                 errorCount++;
//                 errors.push(`Row ${i + 2}: ${result.error}`);
//               }
//             } else {
//               errorCount++;
//               errors.push(`Row ${i + 2}: Missing required field (name)`);
//             }
//           }
//         } catch (rowError) {
//           errorCount++;
//           errors.push(`Row ${i + 2}: ${rowError instanceof Error ? rowError.message : 'Unknown error'}`);
//         }
//       }

//       // Show results
//       if (successCount > 0) {
//         toast({
//           title: "Upload Successful",
//           description: `Successfully imported ${successCount} ${uploadType} to your organization data.${errorCount > 0 ? ` ${errorCount} rows had errors.` : ''}`,
//         });
//       } else {
//         console.error('Upload failed - no successful imports. Errors:', errors);
//         throw new Error(`No data was successfully imported. ${errorCount} rows had errors. Check console for details.`);
//       }

//       if (errorCount > 0) {
//         console.error('Upload errors:', errors);
//       }

//       setFile(null);
//       onSuccess?.();
//     } catch (error) {
//       console.error('Error processing upload:', error);
//       toast({
//         title: "Upload Error",
//         description: `Failed to process upload: ${error instanceof Error ? error.message : 'Unknown error'}`,
//         variant: "destructive"
//       });
//     } finally {
//       setUploading(false);
//     }
//   };

//   return (
//     <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
//       {/* Template Downloads */}
//       <Card>
//         <CardHeader>
//           <CardTitle className="flex items-center gap-2">
//             <Download className="h-5 w-5" />
//             Download Templates
//           </CardTitle>
//           <CardDescription>
//             Download CSV/Excel templates that match the exact library format
//           </CardDescription>
//         </CardHeader>
//         <CardContent className="space-y-4">
//           {/* Upload Type Selection */}
//           <div>
//             <Label htmlFor="upload-type">Data Type</Label>
//             <Select value={uploadType} onValueChange={(value: 'metrics' | 'materials') => setUploadType(value)}>
//               <SelectTrigger id="upload-type">
//                 <SelectValue />
//               </SelectTrigger>
//               <SelectContent>
//                 <SelectItem value="metrics">
//                   <div className="flex items-center gap-2">
//                     <Database className="h-4 w-4" />
//                     Sustainability Metrics
//                   </div>
//                 </SelectItem>
//                 <SelectItem value="materials">
//                   <div className="flex items-center gap-2">
//                     <Package className="h-4 w-4" />
//                     Materials Library
//                   </div>
//                 </SelectItem>
//               </SelectContent>
//             </Select>
//           </div>

//           {/* Template Downloads */}
//           <div className="space-y-3">
//             <div>
//               <Label className="text-sm font-medium">CSV Template</Label>
//               <p className="text-xs text-gray-600 mb-2">
//                 Download a CSV template with the exact column structure for {uploadType}
//               </p>
//               <Button
//                 variant="outline"
//                 size="sm"
//                 className="w-full"
//                 onClick={() => downloadTemplate(uploadType, 'csv')}
//                 disabled={processing}
//               >
//                 <Download className="h-4 w-4 mr-2" />
//                 {processing ? 'Processing...' : `Download ${uploadType} CSV Template`}
//               </Button>
//             </div>

//             <div>
//               <Label className="text-sm font-medium">Excel Template</Label>
//               <p className="text-xs text-gray-600 mb-2">
//                 Download an Excel template with sample data and formatting
//               </p>
//               <Button
//                 variant="outline"
//                 size="sm"
//                 className="w-full"
//                 onClick={() => downloadTemplate(uploadType, 'excel')}
//                 disabled={processing}
//               >
//                 <FileText className="h-4 w-4 mr-2" />
//                 {processing ? 'Processing...' : `Download ${uploadType} Excel Template`}
//               </Button>
//             </div>
//           </div>

//           {/* Template Information */}
//           <Alert>
//             <AlertCircle className="h-4 w-4" />
//             <AlertDescription>
//               <div className="space-y-2">
//                 <p><strong>Template Features:</strong></p>
//                 <ul className="list-disc list-inside text-sm space-y-1">
//                   <li>Exact column structure matching the library</li>
//                   <li>Sample data for reference</li>
//                   <li>Required and optional field indicators</li>
//                   <li>Data validation rules included</li>
//                 </ul>
//               </div>
//             </AlertDescription>
//           </Alert>
//         </CardContent>
//       </Card>

//       {/* File Upload */}
//       <Card>
//         <CardHeader>
//           <CardTitle className="flex items-center gap-2">
//             <Upload className="h-5 w-5" />
//             Upload Data
//           </CardTitle>
//           <CardDescription>
//             Upload your CSV or Excel file with {uploadType} data
//           </CardDescription>
//         </CardHeader>
//         <CardContent>
//           <div className="space-y-4">
//             <div>
//               <Label htmlFor="file-upload">Choose File</Label>
//               <Input
//                 id="file-upload"
//                 type="file"
//                 accept=".csv,.xlsx,.xls"
//                 onChange={handleFileUpload}
//                 className="mt-1"
//               />
//             </div>
            
//             {file && (
//               <div className="flex items-center gap-2 p-3 bg-green-50 rounded-md">
//                 <CheckCircle className="h-4 w-4 text-green-600" />
//                 <span className="text-sm text-green-700">{file.name}</span>
//                 <Badge variant="outline" className="text-xs">
//                   {(file.size / 1024).toFixed(1)} KB
//                 </Badge>
//               </div>
//             )}

//             {/* Tags Section */}
//             <div>
//               <Label className="text-sm font-medium">Apply Tags to All Uploaded Data</Label>
//               <p className="text-xs text-gray-600 mb-2">
//                 Tags will be applied to all {uploadType} data uploaded from this file
//               </p>
//               <DataTaggingComponent
//                 selectedTags={[]}
//                 onTagsChange={() => {}} // Will be implemented when we add tag support to bulk uploads
//                 dataType={uploadType === 'metrics' ? 'metric' : 'material'}
//                 showCreateTag={true}
//               />
//             </div>

//             <Alert>
//               <AlertCircle className="h-4 w-4" />
//               <AlertDescription>
//                 <div className="space-y-2">
//                   <p><strong>Supported formats:</strong> CSV, Excel (.xlsx, .xls)</p>
//                   <p><strong>File size limit:</strong> 10 MB</p>
//                   <p><strong>Required:</strong> Use the template format for proper data structure</p>
//                 </div>
//               </AlertDescription>
//             </Alert>

//             <Button 
//               className="w-full" 
//               disabled={!file || uploading}
//               onClick={processUpload}
//             >
//               {uploading ? (
//                 <>
//                   <Upload className="h-4 w-4 mr-2 animate-spin" />
//                   Processing Upload...
//                 </>
//               ) : (
//                 <>
//                   <Upload className="h-4 w-4 mr-2" />
//                   Process Upload
//                 </>
//               )}
//             </Button>
//           </div>
//         </CardContent>
//       </Card>

//       {/* Upload Guidelines */}
//       <Card className="lg:col-span-2">
//         <CardHeader>
//           <CardTitle>Upload Guidelines</CardTitle>
//         </CardHeader>
//         <CardContent>
//           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//             <div>
//               <h4 className="font-medium mb-3 flex items-center gap-2">
//                 <Database className="h-4 w-4" />
//                 Metrics Upload
//               </h4>
//               <ul className="space-y-2 text-sm">
//                 <li>• Use the provided CSV/Excel template</li>
//                 <li>• Fill in <code>custom_value</code> with your organization's data</li>
//                 <li>• Set <code>reporting_period</code> in YYYY-MM-DD format</li>
//                 <li>• Include <code>data_source</code> for traceability</li>
//                 <li>• Add custom metadata in JSON format if needed</li>
//               </ul>
//             </div>
//             <div>
//               <h4 className="font-medium mb-3 flex items-center gap-2">
//                 <Package className="h-4 w-4" />
//                 Materials Upload
//               </h4>
//               <ul className="space-y-2 text-sm">
//                 <li>• Use the provided CSV/Excel template</li>
//                 <li>• Override library values with <code>custom_*</code> fields</li>
//                 <li>• Set <code>custom_currency</code> for cost data</li>
//                 <li>• Include <code>custom_metadata</code> for additional info</li>
//                 <li>• Ensure all required fields are completed</li>
//               </ul>
//             </div>
//           </div>
//         </CardContent>
//       </Card>
//     </div>
//   );
// };
